import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as PendingIcon,
  PlayArrow as CurrentIcon,
} from '@mui/icons-material';
import type { TicketWorkflowTabProps } from './types';

const TicketWorkflowTab: React.FC<TicketWorkflowTabProps> = ({
  ticket,
  workflow,
  loadingWorkflow,
}) => {
  if (loadingWorkflow) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!workflow) {
    return (
      <Alert severity="warning">
        Workflow-Daten konnten nicht geladen werden.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {workflow.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Chip label={workflow.kategorie} color="primary" size="small" />
          <Chip
            label={`Schritt ${ticket.schrittNummer} von ${workflow.tickets.length}`}
            color="secondary"
            size="small"
          />
          <Chip
            label={workflow.status}
            color={workflow.status === 'Erledigt' ? 'success' : workflow.status === 'In Bearbeitung' ? 'warning' : 'default'}
            size="small"
          />
        </Box>
      </Box>

      <Stepper
        activeStep={ticket.schrittNummer - 1}
        orientation="vertical"
        sx={{ mt: 2 }}
      >
        {workflow.tickets.map((schritt) => {
          const isCurrentStep = schritt.schrittNummer === ticket.schrittNummer;
          const isCompleted = schritt.status === 'Erledigt';

          return (
            <Step key={schritt.ticketID} completed={isCompleted}>
              <StepLabel
                optional={
                  schritt.erledigtAm ? (
                    <Typography variant="caption" color="text.secondary">
                      Erledigt am {new Date(schritt.erledigtAm).toLocaleDateString('de-DE')}
                    </Typography>
                  ) : null
                }
                StepIconComponent={() => (
                  isCompleted ? (
                    <CheckCircleIcon color="success" />
                  ) : isCurrentStep ? (
                    <CurrentIcon color="primary" />
                  ) : (
                    <PendingIcon color="disabled" />
                  )
                )}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: isCurrentStep ? 'bold' : 'normal',
                      color: isCurrentStep ? 'primary.main' : 'text.primary'
                    }}
                  >
                    {schritt.schrittNummer}. {schritt.titel}
                  </Typography>
                  {isCurrentStep && (
                    <Chip label="Aktuell" size="small" color="primary" />
                  )}
                </Box>
              </StepLabel>
              <StepContent>
                <Box sx={{ p: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={schritt.status}
                      size="small"
                      color={schritt.status === 'Erledigt' ? 'success' : schritt.status === 'In Bearbeitung' ? 'warning' : 'default'}
                    />
                    {isCurrentStep && (
                      <Chip
                        label="Dieses Ticket"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default TicketWorkflowTab;
